import React, { useCallback, useEffect, useRef, useState } from "react";
import Drawer from "@mui/material/Drawer";
import { FiBell, FiSearch, FiX } from "react-icons/fi";
import { showErrorToast } from "../../constants/ToastService.js";
import { sprintNotificationsApi } from "./sprintNotificationsApi";
import { dayGroup } from "./notificationUi";
import NotificationRow from "./NotificationRow";

const PAGE_SIZE = 20;
const FILTERS = [
    { key: "", label: "All" },
    { key: "test", label: "Ready to test" },
    { key: "fail", label: "Failed" },
    { key: "pass", label: "Passed" },
];

/** Every notification of one sprint for the logged-in user, newest first. */
const SprintNotificationHistory = ({ open, sprint, count, onClose, onChanged, onOpenNotification }) => {
    const [kind, setKind] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    // Wait for a pause in typing before searching.
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    // Which sprint + filter the list is for; an answer for an older one is dropped.
    const listKey = `${sprint?.id}|${kind}|${search}`;
    const currentKey = useRef(listKey);
    currentKey.current = listKey;

    // Another sprint starts empty rather than showing the old sprint's rows.
    useEffect(() => {
        setItems([]);
        setPage(1);
        setHasMore(false);
        setTotal(0);
    }, [sprint?.id]);

    /**
     * mode "replace" - first page of a new filter
     *      "append"  - Load more
     *      "merge"   - refresh the top after a poll, keeping the pages already loaded
     */
    const load = useCallback(async (nextPage, mode) => {
        if (!sprint?.id) return;
        const key = `${sprint.id}|${kind}|${search}`;
        setLoading(true);
        try {
            const res = await sprintNotificationsApi.getList(sprint.id, { page: nextPage, page_size: PAGE_SIZE, kind, search });
            if (currentKey.current !== key) return;
            if (res.status !== "success") { showErrorToast(res.message); return; }
            const { results, has_more: more, total: all } = res.data;
            setItems(prev => {
                if (mode === "replace") return results;
                // New rows push older ones down a page, so drop repeats and keep newest first.
                const fresh = new Set(results.map(r => r.id));
                return [...results, ...prev.filter(p => !fresh.has(p.id))].sort((a, b) => b.id - a.id);
            });
            if (mode !== "merge") {
                setPage(nextPage);
                setHasMore(more);
            }
            setTotal(all);
        } catch (e) {
            if (currentKey.current === key) showErrorToast("Could not load notification history");
        } finally {
            if (currentKey.current === key) setLoading(false);
        }
    }, [sprint?.id, kind, search]);

    // First page on open and on a filter/search change.
    useEffect(() => {
        if (open) load(1, "replace");
    }, [open, load]);

    // A poll found new ones or something was marked read: refresh the top only.
    useEffect(() => {
        if (open) load(1, "merge");
        // Only a change of count should trigger this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count]);

    const handleMarkAll = async () => {
        try {
            await sprintNotificationsApi.markAllRead(sprint.id);
            setItems(prev => prev.map(i => ({ ...i, is_read: true })));
            onChanged();
        } catch (e) {
            showErrorToast(e.message);
        }
    };

    let lastGroup = null;

    return (
        <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { className: "sn-drawer" } }}>
            <div className="sn-drawer__head">
                <div>
                    <div className="sn-pop__title">Notification history</div>
                    <div className="sn-pop__sub">{sprint?.sprint_name}{total ? ` · ${total} total` : ""}</div>
                </div>
                <button type="button" className="sn-icon-btn" onClick={onClose} aria-label="Close"><FiX size={16} /></button>
            </div>

            <div className="sn-drawer__filters">
                <div className="sn-chips">
                    {FILTERS.map(f => (
                        <button key={f.key || "all"} type="button" className={`sn-chip${kind === f.key ? " sn-chip--on" : ""}`} onClick={() => setKind(f.key)}>
                            {f.label}
                        </button>
                    ))}
                </div>
                <label className="sn-search">
                    <FiSearch size={13} />
                    <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search ticket no. or title" />
                </label>
                {count > 0 && <button type="button" className="sn-link sn-drawer__markall" onClick={handleMarkAll}>Mark all as read</button>}
            </div>

            <div className="sn-drawer__list">
                {!loading && !items.length && (
                    <div className="sn-empty">
                        <FiBell size={22} />
                        <div className="sn-empty__title">{kind || search ? "Nothing matches" : "No notifications yet"}</div>
                        <div className="sn-empty__sub">{kind || search ? "Try another filter or search." : "Status updates for your tickets in this sprint will show here."}</div>
                    </div>
                )}
                {items.map(item => {
                    const group = dayGroup(item.created_time);
                    const heading = group !== lastGroup ? group : null;
                    lastGroup = group;
                    return (
                        <React.Fragment key={item.id}>
                            {heading && <div className="sn-group">{heading}</div>}
                            <NotificationRow item={item} onClick={onOpenNotification} />
                        </React.Fragment>
                    );
                })}
                {hasMore && (
                    <button type="button" className="sn-more" onClick={() => load(page + 1, "append")} disabled={loading}>
                        {loading ? "Loading…" : "Load more"}
                    </button>
                )}
                {loading && !items.length && <div className="sn-empty__sub sn-pad">Loading…</div>}
            </div>
        </Drawer>
    );
};

export default SprintNotificationHistory;
